/**
 * The subject / body box of a member-message workflow step, with the org's
 * variable picker, a preview, and the validate result shown as you type.
 * (The save itself is gated by firstMessageStepVariableProblem.)
 */

import React, { useEffect, useRef, useState } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  TemplateCheckNotice,
  TemplatePreviewButton,
  VariablePicker,
  insertToken,
  templateVariablesApi,
  type ValidateResponse,
} from '@/components/messaging/templateVariables';

interface MessageTemplateFieldProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  singleLine?: boolean;
  /** For the preview: the subject that goes with this body. */
  subject?: string | null;
  /** Show the preview button (the body field does, the subject does not). */
  withPreview?: boolean;
}

const boxClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

export function MessageTemplateField({ value, onChange, placeholder, singleLine, subject, withPreview }: MessageTemplateFieldProps) {
  const { t } = useAppTranslation();
  const ref = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
  const [check, setCheck] = useState<ValidateResponse | null>(null);

  useEffect(() => {
    if (!/\{[^{}]+\}/.test(value || '')) {
      setCheck(null);
      return;
    }
    let cancelled = false;
    const h = window.setTimeout(() => {
      templateVariablesApi
        .validate({ body: value, applies_to: 'messages' })
        .then((res) => !cancelled && setCheck(res))
        .catch(() => !cancelled && setCheck(null));
    }, 600);
    return () => {
      cancelled = true;
      window.clearTimeout(h);
    };
  }, [value]);

  return (
    <div className="space-y-2">
      {singleLine ? (
        <input
          ref={ref as React.RefObject<HTMLInputElement>}
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={boxClass}
        />
      ) : (
        <textarea
          ref={ref as React.RefObject<HTMLTextAreaElement>}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={4}
          className={boxClass}
        />
      )}
      <div className="flex flex-wrap items-center gap-2">
        <VariablePicker
          appliesTo="messages"
          onInsert={(key) => onChange(insertToken(ref.current, value || '', key))}
          label={singleLine ? t('templateVariables.picker.insertInSubject', 'Insert variable in subject') : undefined}
        />
        {withPreview && <TemplatePreviewButton appliesTo="messages" subject={subject} body={value || ''} />}
      </div>
      <TemplateCheckNotice result={check} />
    </div>
  );
}
